import React from 'react';
import { Info, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { NumberField } from '@/components/ui/number-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PARTICIPATION_VS_ATTRIBUTION_NOTE, WHOOP_ACCURACY_DISCLAIMER } from '../data/model';
import type { RoiCompanyInputs } from '../data/model';

interface Props {
  company: RoiCompanyInputs;
  onChange: (next: RoiCompanyInputs) => void;
  onReset: () => void;
}

type NumericKey = Exclude<keyof RoiCompanyInputs, 'participationRate' | 'attributionRate'>;

interface FieldDef {
  key: NumericKey;
  label: string;
  prefix?: string;
  step?: number;
  // Unlike the lever conversions in model.ts, none of these defaults trace to
  // a specific published study — they're generic starting assumptions, so the
  // tooltip says that plainly rather than inventing a citation.
  tooltip: string;
}

const FIELDS: FieldDef[] = [
  {
    key: 'employees',
    label: 'Employees in the programme',
    tooltip: 'Default headcount assumption. Replace with the actual number of employees enrolled.',
  },
  {
    key: 'averageSalary',
    label: 'Fully loaded average staff cost / yr',
    prefix: '£',
    tooltip: 'A general starting assumption. Replace with your organisation’s actual fully loaded average salary.',
  },
  {
    key: 'programmeCostPerEmployee',
    label: 'Programme cost / employee / yr',
    prefix: '£',
    tooltip: 'Default Peak4 programme cost assumption. Confirm against your organisation’s actual contract rate.',
  },
  {
    key: 'costPerSickDay',
    label: 'Cost per sickness day',
    prefix: '£',
    tooltip:
      'A general starting assumption for the cost of one lost working day. Replace with your own figure if known.',
  },
  {
    key: 'absenceDaysPerEmployee',
    label: 'Average absence days / employee / yr',
    step: 0.1,
    tooltip: 'A general starting assumption. Replace with your organisation’s actual average sickness-absence days.',
  },
  {
    key: 'injuriesPerYear',
    label: 'Workplace injuries per year',
    tooltip: 'Default assumption. Replace with your organisation’s actual annual workplace injury count.',
  },
  {
    key: 'costPerInjury',
    label: 'Cost per injury',
    prefix: '£',
    tooltip:
      'A general starting assumption for the average cost of one workplace injury. Replace with your own figure if known.',
  },
];

export const AssumptionsSection: React.FC<Props> = ({ company, onChange, onReset }) => {
  const setNumeric = (key: NumericKey, value: number) => {
    onChange({ ...company, [key]: Number.isFinite(value) && value >= 0 ? value : 0 });
  };

  const setRate = (key: 'participationRate' | 'attributionRate', pct: number) => {
    onChange({ ...company, [key]: pct / 100 });
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-base font-semibold'>Your company numbers</h2>
          <p className='text-sm text-muted-foreground'>
            Tune to your organisation. Participation rate should reflect the live figure from the Management Hub.
          </p>
        </div>
        <Button variant='outline' size='sm' onClick={onReset}>
          <RotateCcw />
          Reset defaults
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workforce & cost inputs</CardTitle>
        </CardHeader>
        <CardContent>
          <TooltipProvider delayDuration={200}>
            <div className='grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
              {FIELDS.map((f) => (
                <div key={f.key} className='flex flex-col gap-1.5'>
                  <div className='flex items-center gap-1'>
                    <Label htmlFor={f.key}>{f.label}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type='button'
                          className='text-muted-foreground hover:text-foreground'
                          aria-label={`About ${f.label}`}
                        >
                          <Info className='size-3' />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{f.tooltip}</TooltipContent>
                    </Tooltip>
                  </div>
                  <NumberField
                    id={f.key}
                    value={company[f.key]}
                    onChange={(v) => setNumeric(f.key, v)}
                    min={0}
                    step={f.step ?? 1}
                    prefix={f.prefix}
                  />
                </div>
              ))}
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* The two rates are a matched pair and each holds one short field, so
          they sit side by side rather than as two near-empty full-width rows. */}
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <Card className='flex flex-col'>
          <CardHeader>
            <CardTitle>Participation rate</CardTitle>
            <CardDescription>
              Share of employees actively engaging with the programme. Pull this from the Management Hub's participation
              figure rather than estimating it.
            </CardDescription>
          </CardHeader>
          <CardContent className='mt-auto'>
            <div className='w-40'>
              <NumberField
                value={Math.round(company.participationRate * 100)}
                onChange={(v) => setRate('participationRate', v)}
                min={0}
                max={100}
                suffix='%'
                aria-label='Participation rate'
              />
            </div>
          </CardContent>
        </Card>

        <Card className='flex flex-col'>
          <CardHeader>
            <CardTitle>Attribution rate</CardTitle>
            <CardDescription>
              Share of any improvement you credit to the programme rather than other causes. A judgement call the
              company makes, not a published figure.
            </CardDescription>
          </CardHeader>
          <CardContent className='mt-auto'>
            <div className='w-40'>
              <NumberField
                value={Math.round(company.attributionRate * 100)}
                onChange={(v) => setRate('attributionRate', v)}
                min={0}
                max={100}
                suffix='%'
                aria-label='Attribution rate'
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Alert>
        <AlertDescription>{PARTICIPATION_VS_ATTRIBUTION_NOTE}</AlertDescription>
      </Alert>

      <Alert>
        <AlertDescription className='text-xs'>{WHOOP_ACCURACY_DISCLAIMER}</AlertDescription>
      </Alert>
    </div>
  );
};
